/**
 * AI Workflow Preset Management Page
 *
 * Admin page for managing system and custom AI workflow presets.
 * System presets are read-only; custom presets support full CRUD.
 * Uses Card/Paper patterns matching TaskManagement/BookingManagement conventions.
 */

import { useState, useMemo } from 'react';
import {
  Container,
  Title,
  Text,
  Stack,
  Group,
  Grid,
  Card,
  Button,
  Table,
  Badge,
  Modal,
  TextInput,
  Select,
  Textarea,
  NumberInput,
  Loader,
  Alert,
  Tooltip,
  ThemeIcon,
  Divider,
  HtmlEditor,
} from '@/components/ui';
import { ActionIcon } from '@mantine/core';
import {
  IconPlus,
  IconPencil,
  IconTrash,
  IconRefresh,
  IconAlertCircle,
  IconArrowLeft,
  IconLock,
  IconTemplate,
  IconSettings,
  IconUser,
  IconForms,
} from '@tabler/icons-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useAiWorkflowPresetService } from '@actschurch/ccms-sdk';
import { toast } from '@/components/ui/providers/AlertProvider';
import type {
  AiWorkflowPreset,
  CreateAiWorkflowPresetRequest,
  UpdateAiWorkflowPresetRequest,
} from '@actschurch/ccms-sdk';

const INPUT_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'file', label: 'File' },
  { value: 'image', label: 'Image' },
  { value: 'structured', label: 'Structured Data' },
  { value: 'multi', label: 'Multi-Input' },
];

const TARGET_RECORD_TYPES = [
  { value: 'task', label: 'Task' },
  { value: 'member', label: 'Member' },
  { value: 'vendor', label: 'Vendor' },
  { value: 'event', label: 'Event' },
  { value: 'project', label: 'Project' },
  { value: 'product', label: 'Product' },
  { value: 'order', label: 'Order' },
  { value: 'general', label: 'General' },
];

interface PresetFormValues {
  vendorID: number | null;
  name: string;
  description: string;
  inputType: string;
  targetRecordType: string;
  steps: string;
  systemPrompt: string;
  jsonSchemaGuide: string;
  sortOrder: number;
}

const DEFAULT_FORM: PresetFormValues = {
  vendorID: null,
  name: '',
  description: '',
  inputType: '',
  targetRecordType: '',
  steps: '[]',
  systemPrompt: '',
  jsonSchemaGuide: '{}',
  sortOrder: 0,
};

export default function PresetsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const service = useAiWorkflowPresetService();

  const [modalOpened, setModalOpened] = useState(false);
  const [deleteModalOpened, setDeleteModalOpened] = useState(false);
  const [editingPreset, setEditingPreset] = useState<AiWorkflowPreset | null>(null);
  const [deletingPreset, setDeletingPreset] = useState<AiWorkflowPreset | null>(null);
  const [form, setForm] = useState<PresetFormValues>(DEFAULT_FORM);

  // Fetch all presets
  const {
    data: presets = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['ai-workflow-presets'],
    queryFn: () => service.getAll(),
  });

  // Stat computations
  const stats = useMemo(() => {
    const systemPresets = presets.filter((p) => p.isSystem).length;
    const customPresets = presets.filter((p) => !p.isSystem).length;
    const inputTypes = new Set(presets.map((p) => p.inputType)).size;
    return { total: presets.length, system: systemPresets, custom: customPresets, inputTypes };
  }, [presets]);

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: CreateAiWorkflowPresetRequest) => service.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-workflow-presets'] });
      toast.success('Preset created');
      closeModal();
    },
    onError: (err: Error) => {
      toast.error('Could not create the preset', err.message);
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateAiWorkflowPresetRequest }) =>
      service.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-workflow-presets'] });
      toast.success('Preset saved');
      closeModal();
    },
    onError: (err: Error) => {
      toast.error('Could not save the preset', err.message);
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: number) => service.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-workflow-presets'] });
      toast.success('Preset deleted');
      setDeleteModalOpened(false);
      setDeletingPreset(null);
    },
    onError: (err: Error) => {
      toast.error('Could not delete the preset', err.message);
    },
  });

  const openCreateModal = () => {
    setEditingPreset(null);
    setForm(DEFAULT_FORM);
    setModalOpened(true);
  };

  const openEditModal = (preset: AiWorkflowPreset) => {
    setEditingPreset(preset);
    setForm({
      vendorID: preset.vendorID ?? null,
      name: preset.name,
      description: preset.description || '',
      inputType: preset.inputType,
      targetRecordType: preset.targetRecordType,
      steps: preset.steps,
      systemPrompt: preset.systemPrompt,
      jsonSchemaGuide: preset.jsonSchemaGuide,
      sortOrder: preset.sortOrder,
    });
    setModalOpened(true);
  };

  const closeModal = () => {
    setModalOpened(false);
    setEditingPreset(null);
    setForm(DEFAULT_FORM);
  };

  const handleSubmit = () => {
    const payload: CreateAiWorkflowPresetRequest = {
      vendorID: form.vendorID,
      name: form.name,
      description: form.description || null,
      inputType: form.inputType,
      targetRecordType: form.targetRecordType,
      steps: form.steps,
      systemPrompt: form.systemPrompt,
      jsonSchemaGuide: form.jsonSchemaGuide,
      sortOrder: form.sortOrder,
    };

    if (editingPreset) {
      updateMutation.mutate({ id: editingPreset.presetID, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  // Sort: system presets first, then by sortOrder
  const sortedPresets = [...presets].sort((a, b) => {
    if (a.isSystem !== b.isSystem) return a.isSystem ? -1 : 1;
    return a.sortOrder - b.sortOrder;
  });

  return (
    <Container fluid py="xl">
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between" align="flex-start">
          <Group gap="sm">
            <ActionIcon variant="subtle" onClick={() => navigate('/admin/ai')}>
              <IconArrowLeft size={20} />
            </ActionIcon>
            <div>
              <Title order={2}>Workflow Presets</Title>
              <Text c="dimmed" size="sm">
                Manage system and custom AI workflow presets.
              </Text>
            </div>
          </Group>
        </Group>

        {/* Stat Cards */}
        <Grid>
          <Grid.Col span={{ base: 6, md: 3 }}>
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Group wrap="nowrap">
                <ThemeIcon size={48} radius="md" variant="light" color="blue">
                  <IconTemplate size={24} />
                </ThemeIcon>
                <div>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                    Total Presets
                  </Text>
                  <Text size="xl" fw={700}>
                    {stats.total}
                  </Text>
                </div>
              </Group>
            </Card>
          </Grid.Col>
          <Grid.Col span={{ base: 6, md: 3 }}>
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Group wrap="nowrap">
                <ThemeIcon size={48} radius="md" variant="light" color="yellow">
                  <IconSettings size={24} />
                </ThemeIcon>
                <div>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                    System Presets
                  </Text>
                  <Text size="xl" fw={700}>
                    {stats.system}
                  </Text>
                </div>
              </Group>
            </Card>
          </Grid.Col>
          <Grid.Col span={{ base: 6, md: 3 }}>
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Group wrap="nowrap">
                <ThemeIcon size={48} radius="md" variant="light" color="green">
                  <IconUser size={24} />
                </ThemeIcon>
                <div>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                    Custom Presets
                  </Text>
                  <Text size="xl" fw={700}>
                    {stats.custom}
                  </Text>
                </div>
              </Group>
            </Card>
          </Grid.Col>
          <Grid.Col span={{ base: 6, md: 3 }}>
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Group wrap="nowrap">
                <ThemeIcon size={48} radius="md" variant="light" color="violet">
                  <IconForms size={24} />
                </ThemeIcon>
                <div>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                    Input Types
                  </Text>
                  <Text size="xl" fw={700}>
                    {stats.inputTypes}
                  </Text>
                </div>
              </Group>
            </Card>
          </Grid.Col>
        </Grid>

        {/* Error */}
        {error && (
          <Alert color="red" icon={<IconAlertCircle size={16} />} title="Error" withCloseButton>
            {(error as Error).message || 'Failed to load presets'}
          </Alert>
        )}

        {/* Loading */}
        {isLoading && (
          <Group justify="center" p="xl">
            <Loader size="lg" />
          </Group>
        )}

        {/* Table in Card */}
        {!isLoading && (
          <Card shadow="sm" padding={0} radius="md" withBorder>
            {/* Card Header */}
            <Group justify="space-between" p="lg" pb="sm">
              <Group gap="xs">
                <IconTemplate size={20} />
                <Title order={4}>Presets</Title>
              </Group>
              <Group gap="xs">
                <Button
                  variant="subtle"
                  size="sm"
                  leftSection={<IconRefresh size={16} />}
                  onClick={() => refetch()}
                  loading={isLoading}
                >
                  Refresh
                </Button>
                <Button size="sm" leftSection={<IconPlus size={16} />} onClick={openCreateModal}>
                  New Preset
                </Button>
              </Group>
            </Group>
            <Divider />

            <Table tableVariant="striped">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Name</Table.Th>
                  <Table.Th>Input Type</Table.Th>
                  <Table.Th>Target Record</Table.Th>
                  <Table.Th>System</Table.Th>
                  <Table.Th>Active</Table.Th>
                  <Table.Th>Order</Table.Th>
                  <Table.Th>Actions</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {sortedPresets.length === 0 && (
                  <Table.Tr>
                    <Table.Td colSpan={7}>
                      <Text ta="center" c="dimmed" py="lg">
                        No presets found. Click "New Preset" to create one.
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                )}
                {sortedPresets.map((preset) => (
                  <Table.Tr key={preset.presetID}>
                    <Table.Td>
                      <Stack gap={2}>
                        <Text size="sm" fw={500}>
                          {preset.name}
                        </Text>
                        {preset.description && (
                          <Text size="xs" c="dimmed" lineClamp={1}>
                            {preset.description}
                          </Text>
                        )}
                      </Stack>
                    </Table.Td>
                    <Table.Td>
                      <Badge variant="light" color="cyan" size="sm">
                        {preset.inputType}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Badge variant="light" color="violet" size="sm">
                        {preset.targetRecordType}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      {preset.isSystem ? (
                        <Badge variant="light" color="blue" size="sm" leftSection={<IconLock size={10} />}>
                          System
                        </Badge>
                      ) : (
                        <Badge color="gray" variant="light" size="sm">
                          Custom
                        </Badge>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Badge color={preset.isActive ? 'green' : 'red'} variant="dot" size="sm">
                        {preset.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{preset.sortOrder}</Text>
                    </Table.Td>
                    <Table.Td>
                      {preset.isSystem ? (
                        <Tooltip label="System presets cannot be modified">
                          <ActionIcon variant="subtle" color="gray" disabled>
                            <IconLock size={16} />
                          </ActionIcon>
                        </Tooltip>
                      ) : (
                        <Group gap="xs">
                          <ActionIcon
                            variant="subtle"
                            color="blue"
                            onClick={() => openEditModal(preset)}
                          >
                            <IconPencil size={16} />
                          </ActionIcon>
                          <ActionIcon
                            variant="subtle"
                            color="red"
                            onClick={() => {
                              setDeletingPreset(preset);
                              setDeleteModalOpened(true);
                            }}
                          >
                            <IconTrash size={16} />
                          </ActionIcon>
                        </Group>
                      )}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Card>
        )}
      </Stack>

      {/* Create/Edit Modal */}
      <Modal
        opened={modalOpened}
        onClose={closeModal}
        title={editingPreset ? 'Edit Workflow Preset' : 'Create Workflow Preset'}
        size="xl"
      >
        <Stack gap="md">
          <TextInput
            label="Name"
            placeholder="Preset name"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.currentTarget.value }))}
            required
          />
          <HtmlEditor
            label="Description"
            placeholder="Optional description"
            value={form.description}
            onChange={(_html, text) => setForm((f) => ({ ...f, description: text }))}
            minHeight={80}
          />
          <Group grow>
            <Select
              label="Input Type"
              placeholder="Select input type"
              data={INPUT_TYPES}
              value={form.inputType}
              onChange={(val) => setForm((f) => ({ ...f, inputType: val || '' }))}
              required
              searchable
              allowDeselect={false}
            />
            <Select
              label="Target Record Type"
              placeholder="Select target record"
              data={TARGET_RECORD_TYPES}
              value={form.targetRecordType}
              onChange={(val) => setForm((f) => ({ ...f, targetRecordType: val || '' }))}
              required
              searchable
              allowDeselect={false}
            />
          </Group>
          <Group grow>
            <NumberInput
              label="Vendor ID"
              description="Leave empty for global preset"
              value={form.vendorID ?? ''}
              onChange={(val) =>
                setForm((f) => ({ ...f, vendorID: val === '' ? null : Number(val) }))
              }
              min={1}
            />
            <NumberInput
              label="Sort Order"
              value={form.sortOrder}
              onChange={(val) => setForm((f) => ({ ...f, sortOrder: Number(val) || 0 }))}
              min={0}
            />
          </Group>
          <HtmlEditor
            label="System Prompt"
            description="The system prompt template for this workflow"
            placeholder="You are an AI assistant that..."
            value={form.systemPrompt}
            onChange={(_html, text) => setForm((f) => ({ ...f, systemPrompt: text }))}
            minHeight={140}
          />
          <Textarea
            label="Steps (JSON)"
            description="Workflow steps as a JSON array"
            placeholder='[{"step": 1, "action": "extract"}, ...]'
            value={form.steps}
            onChange={(e) => setForm((f) => ({ ...f, steps: e.currentTarget.value }))}
            minRows={3}
            autosize
            maxRows={8}
          />
          <Textarea
            label="JSON Schema Guide"
            description="JSON schema describing the expected output format"
            placeholder='{"type": "object", "properties": {...}}'
            value={form.jsonSchemaGuide}
            onChange={(e) => setForm((f) => ({ ...f, jsonSchemaGuide: e.currentTarget.value }))}
            minRows={3}
            autosize
            maxRows={8}
          />
          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={closeModal}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              loading={createMutation.isPending || updateMutation.isPending}
              disabled={!form.name || !form.inputType || !form.targetRecordType || !form.systemPrompt}
            >
              {editingPreset ? 'Update' : 'Create'}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        opened={deleteModalOpened}
        onClose={() => {
          setDeleteModalOpened(false);
          setDeletingPreset(null);
        }}
        title="Delete Workflow Preset"
        size="sm"
      >
        <Stack gap="md">
          <Text>
            Are you sure you want to delete the preset{' '}
            <Text span fw={600}>
              {deletingPreset?.name}
            </Text>
            ? This action cannot be undone.
          </Text>
          <Group justify="flex-end">
            <Button
              variant="default"
              onClick={() => {
                setDeleteModalOpened(false);
                setDeletingPreset(null);
              }}
            >
              Cancel
            </Button>
            <Button
              color="red"
              loading={deleteMutation.isPending}
              onClick={() => {
                if (deletingPreset) {
                  deleteMutation.mutate(deletingPreset.presetID);
                }
              }}
            >
              Delete
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
}
